import PositionPlanCard from "@/components/PositionPlanCard";

/**
 * Order Dispatch sidebar: replay controls, risk slider, BUY/SELL,
 * plan tools, reset/CSV, starting capital select.
 */
export default function OrderPanel({
  mode, autoplay, setAutoplay, speed, setSpeed,
  streamIndex, mainLen, advanceBar, refetch,
  riskPct, setRiskPct, position, plan,
  onExecute, onLiquidate, onStartPlan, onExecutePlan, onDiscardPlan,
  onResetSession, onExportCSV, onSetCapital,
  lastClose, balance,
}) {
  return (
    <div className="p-4 space-y-3 border-b border-slate-800">
      {mode === "replay" && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
            <i className="fa-solid fa-bolt text-yellow-500"/> Replay Stream
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="next-bar-btn" onClick={advanceBar}
              className="py-2 px-3 bg-slate-800 hover:bg-slate-700 border border-slate-700/60 text-xs font-bold rounded-xl text-white transition-all">
              <i className="fa-solid fa-forward-step mr-1"/> Next Bar
            </button>
            <button data-testid="autoplay-btn" onClick={() => setAutoplay(a => !a)}
              className={`py-2 px-3 border text-xs font-bold rounded-xl transition-all ${autoplay ? "bg-amber-600/10 border-amber-500/30 text-amber-400" : "bg-blue-600/10 border-blue-500/30 text-blue-400"}`}>
              <i className={`fa-solid ${autoplay ? "fa-pause" : "fa-play"} mr-1`}/> {autoplay ? "Pause" : "Autoplay"}
            </button>
          </div>
          <label className="flex justify-between text-[11px] font-bold text-slate-500 mt-2 mb-1">
            <span>VELOCITY</span><span className="text-blue-400 font-bold">{speed}ms</span>
          </label>
          <input data-testid="speed-slider" type="range" min="50" max="1500" step="25" value={speed}
            onChange={e => setSpeed(parseInt(e.target.value))}
            className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer"/>
        </div>
      )}
      {mode === "live" && (
        <div className="text-[11px] bg-emerald-600/10 border border-emerald-500/30 text-emerald-300 rounded-lg p-2">
          <i className="fa-solid fa-tower-broadcast mr-1"/> DELAY MODE — polling every 30s (~15min delayed).
          <button data-testid="refresh-live-btn" onClick={refetch} className="ml-2 underline">Force refresh</button>
        </div>
      )}

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
          <i className="fa-solid fa-cash-register text-emerald-500"/> Order Dispatch
        </h3>
        <label className="flex justify-between text-[11px] font-bold text-slate-500 mb-1">
          <span>RISK / TRADE</span><span className="text-amber-400 font-bold">{riskPct.toFixed(2)}%</span>
        </label>
        <input data-testid="risk-slider" type="range" min="0.25" max="5" step="0.25" value={riskPct}
          onChange={e => setRiskPct(parseFloat(e.target.value))}
          className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer mb-2"/>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="buy-btn" onClick={() => onExecute("BUY")} disabled={!!position}
            className="py-2.5 px-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 active:scale-95 text-xs font-bold rounded-xl text-white transition-all">
            <i className="fa-solid fa-arrow-trend-up mr-1"/> BUY
          </button>
          <button data-testid="sell-btn" onClick={() => onExecute("SELL")} disabled={!!position}
            className="py-2.5 px-3 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 active:scale-95 text-xs font-bold rounded-xl text-white transition-all">
            <i className="fa-solid fa-arrow-trend-down mr-1"/> SELL
          </button>
        </div>
        <button data-testid="liquidate-btn" onClick={onLiquidate} disabled={!position}
          className="w-full mt-2 py-2 px-4 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 border border-slate-700 text-xs font-bold rounded-xl text-slate-300 transition-all">
          LIQUIDATE
        </button>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button data-testid="plan-long-btn" onClick={() => onStartPlan("LONG")} disabled={!!position}
            className={`py-1.5 px-2 hover:bg-emerald-600/20 disabled:opacity-40 border text-[10px] font-bold rounded-xl text-emerald-300 uppercase ${plan?.side === "LONG" ? "bg-emerald-600/30 border-emerald-400 ring-1 ring-emerald-400/50" : "bg-emerald-600/10 border-emerald-500/30"}`}>
            <i className="fa-solid fa-ruler-vertical mr-1"/> Plan Long
          </button>
          <button data-testid="plan-short-btn" onClick={() => onStartPlan("SHORT")} disabled={!!position}
            className={`py-1.5 px-2 hover:bg-rose-600/20 disabled:opacity-40 border text-[10px] font-bold rounded-xl text-rose-300 uppercase ${plan?.side === "SHORT" ? "bg-rose-600/30 border-rose-400 ring-1 ring-rose-400/50" : "bg-rose-600/10 border-rose-500/30"}`}>
            <i className="fa-solid fa-ruler-vertical mr-1"/> Plan Short
          </button>
        </div>
        {plan && !position && (
          <div className="mt-2">
            <PositionPlanCard plan={plan} lastPrice={lastClose || plan.entryPrice}
              balance={balance} riskPct={riskPct}
              onExecute={onExecutePlan} onDiscard={onDiscardPlan}/>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button data-testid="reset-session-btn" onClick={onResetSession}
            className="py-1.5 px-2 bg-rose-900/30 hover:bg-rose-900/50 border border-rose-800/40 text-[10px] font-bold rounded-xl text-rose-300 uppercase">
            Reset PnL
          </button>
          <button data-testid="export-csv-btn" onClick={onExportCSV}
            className="py-1.5 px-2 bg-blue-900/30 hover:bg-blue-900/50 border border-blue-800/40 text-[10px] font-bold rounded-xl text-blue-300 uppercase">
            <i className="fa-solid fa-file-csv mr-1"/> CSV
          </button>
        </div>
        <div className="mt-2">
          <label className="text-[11px] font-bold text-slate-500 mb-1 block uppercase">Starting capital</label>
          <select data-testid="capital-select"
            onChange={e => { const v = parseInt(e.target.value); if (v) onSetCapital(v); e.target.value = ""; }}
            defaultValue=""
            className="w-full bg-[#161d28] border border-slate-700 text-xs font-bold text-amber-300 rounded-md px-2 py-1 cursor-pointer">
            <option value="" disabled>Pick a starting balance…</option>
            <option value="10000">$10,000</option>
            <option value="25000">$25,000</option>
            <option value="50000">$50,000</option>
            <option value="100000">$100,000</option>
            <option value="150000">$150,000</option>
          </select>
        </div>
        {position && (
          <div className="mt-2 text-[10px] text-slate-400 bg-slate-900/60 border border-slate-800 rounded p-2">
            <i className="fa-solid fa-hand-pointer text-amber-400 mr-1"/> Drag the <span className="text-rose-300">SL</span> / <span className="text-emerald-300">TP</span> lines on chart.
          </div>
        )}
      </div>
    </div>
  );
}
